/*
 * Companion script for the MODERN cart drawer (snippets/cart-drawer-modern.liquid).
 *
 * Two independent features, both opt-in from the section settings and both read
 * from window.cartDrawerModern, which the snippet emits:
 *
 *   1. Free-shipping unlock confetti. Fires on the locked -> unlocked
 *      transition, and once on open if the cart is already over the threshold.
 *
 *   2. Shipping price. Reuses the ZIP the shopper has already given the site
 *      elsewhere (localStorage `HHUserZipCode`, written by the growing-zone
 *      modal in assets/custom.js.liquid), asks for one inline when there isn't,
 *      and quotes the cheapest real rate from Shopify's
 *      /cart/shipping_rates.json as a line above the subtotal.
 *
 * Neither feature mutates the cart: the rates endpoint is read-only and nothing
 * here touches a line item, so everything assets/cart.js owns is left alone.
 */
(function () {
  'use strict';

  var ZIP_STORAGE_KEY = 'HHUserZipCode';

  function config() {
    return window.cartDrawerModern || {};
  }

  function drawer() {
    return document.querySelector('cart-drawer.cart-drawer--modern');
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* --------------------------------------------------------------- confetti */

  var PARTICLE_COUNT = 90;
  var MAX_FRAMES = 220;
  // Bees outlast the confetti so the burst settles into them rather than
  // everything stopping at once — they are still circling as the last paper
  // falls, then drift off the edges.
  var BEE_FRAMES = 310;
  var BEE_FADE_FRAMES = 45;
  var animationId = null;
  var wasUnlocked = null;

  // Decoded once and reused: re-decoding on every unlock would drop frames on
  // the first burst after each cart change.
  var beeImage = null;
  var beeImageState = 'idle';

  function loadBeeImage() {
    var src = config().beeImage;
    if (!src || beeImageState !== 'idle') return;

    beeImageState = 'loading';
    var img = new Image();
    img.decoding = 'async';
    img.onload = function () {
      beeImage = img;
      beeImageState = 'ready';
    };
    img.onerror = function () {
      beeImageState = 'failed';
    };
    img.src = src;
  }

  /**
   * A bee wanders rather than falling: it drifts across the card on a slow
   * heading, bobbing on a sine so the path reads as flight, and turns gently so
   * it never tracks perfectly straight. Sprites are drawn facing their direction
   * of travel, mirrored rather than rotated past vertical so a bee flying left is
   * never upside down.
   */
  function makeBees(width, height, count) {
    var bees = [];
    for (var i = 0; i < count; i++) {
      var fromLeft = Math.random() < 0.5;
      var size = 26 + Math.random() * 18;
      bees.push({
        x: fromLeft ? -size - Math.random() * width * 0.3 : width + size + Math.random() * width * 0.3,
        y: height * (0.15 + Math.random() * 0.6),
        size: size,
        speed: (0.9 + Math.random() * 1.1) * (fromLeft ? 1 : -1),
        drift: (Math.random() - 0.5) * 0.5,
        bobPhase: Math.random() * Math.PI * 2,
        bobSpeed: 0.06 + Math.random() * 0.06,
        bobAmount: 6 + Math.random() * 10,
        turnPhase: Math.random() * Math.PI * 2,
        turnSpeed: 0.012 + Math.random() * 0.02,
      });
    }
    return bees;
  }

  function drawBees(ctx, bees, frame, width, height) {
    if (!beeImage) return;

    var ratio = beeImage.naturalHeight / beeImage.naturalWidth || 1;
    // Fade out over the closing stretch rather than vanishing mid-air.
    var fade = frame > BEE_FRAMES - BEE_FADE_FRAMES ? Math.max(0, (BEE_FRAMES - frame) / BEE_FADE_FRAMES) : 1;

    bees.forEach(function (b) {
      b.turnPhase += b.turnSpeed;
      b.x += b.speed + Math.sin(b.turnPhase) * 0.6;
      b.y += b.drift + Math.sin(frame * b.bobSpeed + b.bobPhase) * 0.5;

      // Wrap rather than pile up at an edge, so the swarm keeps circulating for
      // the whole run instead of parking off-screen after a second.
      var margin = b.size * 2;
      if (b.x < -margin) b.x = width + margin;
      if (b.x > width + margin) b.x = -margin;
      if (b.y < -margin) b.y = height * 0.8;
      if (b.y > height + margin) b.y = height * 0.1;

      var w = b.size;
      var h = b.size * ratio;
      var bob = Math.sin(frame * b.bobSpeed + b.bobPhase) * b.bobAmount;

      ctx.save();
      ctx.globalAlpha = fade;
      ctx.translate(b.x, b.y + bob);
      // Mirror for leftward flight; a rotation would turn the bee upside down.
      if (b.speed < 0) ctx.scale(-1, 1);
      ctx.rotate(Math.sin(b.turnPhase) * 0.18);
      ctx.drawImage(beeImage, -w / 2, -h / 2, w, h);
      ctx.restore();
    });
  }

  function confettiColors() {
    // Read from the drawer so the burst uses the live colour scheme rather than
    // a second hard-coded palette that would drift from the theme.
    var host = drawer();
    var fallback = ['#4F533A', '#A68A61', '#975102', '#EBE2D0', '#3C3F2C'];
    if (!host) return fallback;

    var styles = getComputedStyle(host);
    var picked = ['--color-primary', '--color-foreground', '--color-button', '--color-secondary-background']
      .map(function (name) {
        var raw = styles.getPropertyValue(name).trim();
        // The theme stores these as bare "r,g,b" triplets, not colour values.
        return /^\d+\s*,\s*\d+\s*,\s*\d+$/.test(raw) ? 'rgb(' + raw + ')' : null;
      })
      .filter(Boolean);

    return picked.length ? picked : fallback;
  }

  /**
   * The goal element toggles `free-shipping-goal--done` on itself from
   * assets/cart.js (FreeShippingGoal.updateShippingGloal). That class is the
   * single source of truth for whether shipping is unlocked.
   * Returns null when there is no goal element at all, so a drawer with the
   * tracker switched off never fires anything.
   */
  function readUnlockedState() {
    var host = drawer();
    if (!host) return null;
    var goal = host.querySelector('free-shipping-goal');
    if (!goal) return null;
    return goal.classList.contains('free-shipping-goal--done');
  }

  function fireConfetti() {
    if (config().confetti !== true) return;
    if (prefersReducedMotion()) return;

    var host = drawer();
    if (!host) return;

    // Re-queried every time: the canvas lives inside #CartDrawer-{id}, which is
    // replaced wholesale by a section refresh, so a cached reference goes stale.
    var canvas = host.querySelector('[data-cart-modern-confetti]');
    if (!canvas || typeof canvas.getContext !== 'function') return;

    var card = host.querySelector('.cart-drawer--modern__card') || canvas.parentElement;
    var width = (card && card.clientWidth) || 440;
    var height = (card && card.clientHeight) || window.innerHeight;
    if (width <= 0 || height <= 0) return;

    canvas.width = width;
    canvas.height = height;

    var ctx = canvas.getContext('2d');
    var colors = confettiColors();
    var particles = [];
    var i;

    for (i = 0; i < PARTICLE_COUNT; i++) {
      particles.push({
        x: Math.random() * width,
        // Staggered above the card so the fall arrives as a shower, not a wall.
        y: -15 - Math.random() * 120,
        width: 5 + Math.random() * 6,
        height: 9 + Math.random() * 7,
        color: colors[Math.floor(Math.random() * colors.length)],
        vx: (Math.random() - 0.5) * 2.2,
        vy: 2.6 + Math.random() * 3.8,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        oscillationSpeed: 0.03 + Math.random() * 0.05,
        oscillationAmplitude: 1.2 + Math.random() * 1.8,
      });
    }

    var bees = beeImage ? makeBees(width, height, 5) : [];

    if (animationId) cancelAnimationFrame(animationId);
    var frame = 0;

    function step() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;
      var alive = 0;

      particles.forEach(function (p) {
        if (p.y > canvas.height + 40) return;
        alive++;

        p.y += p.vy;
        p.x += p.vx + Math.sin(frame * p.oscillationSpeed) * p.oscillationAmplitude;
        p.rotation += p.rotationSpeed;

        ctx.save();
        ctx.translate(p.x + p.width / 2, p.y + p.height / 2);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        // Fade the last third of the run out so the burst ends rather than stops.
        ctx.globalAlpha =
          frame > MAX_FRAMES * 0.66 ? Math.max(0, 1 - (frame - MAX_FRAMES * 0.66) / (MAX_FRAMES * 0.34)) : 1;
        ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
        ctx.restore();
      });

      var beesFlying = bees.length > 0 && frame < BEE_FRAMES;
      if (beesFlying) drawBees(ctx, bees, frame, width, height);

      // Runs until BOTH are finished: the confetti settling first is the point,
      // so the loop cannot stop on the paper alone.
      if ((alive > 0 && frame < MAX_FRAMES) || beesFlying) {
        animationId = requestAnimationFrame(step);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        animationId = null;
      }
    }

    animationId = requestAnimationFrame(step);
  }

  /**
   * Fires only on the locked -> unlocked transition, never on every render.
   * `wasUnlocked` starts as null so the first reading only records the baseline:
   * a shopper opening a drawer that is already over the threshold gets the burst
   * from checkOpenState() instead, not from this.
   */
  function checkTransition() {
    var isUnlocked = readUnlockedState();
    if (isUnlocked === null) return;
    if (isUnlocked && wasUnlocked === false) fireConfetti();
    wasUnlocked = isUnlocked;
  }

  function checkOpenState() {
    if (readUnlockedState() === true) fireConfetti();
  }

  /**
   * `cart:updated` has more than one subscriber, and the one that matters here —
   * FreeShippingGoal in assets/cart.js, which recomputes the unlocked class — is
   * not guaranteed to have run before this file's listener. Reading the class in
   * the same tick would sample the previous state and miss the unlock entirely,
   * so the check is deferred past the whole listener list (and past the class
   * write) rather than racing it.
   */
  var settleTimer = null;
  var RENDER_SETTLE_MS = 120;

  function scheduleTransitionCheck() {
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(checkTransition, RENDER_SETTLE_MS);
  }

  /* -------------------------------------------------------- shipping rates */

  /*
   * The shopper's ZIP buys a real shipping price, not a guessed delivery window.
   * Rates come from Shopify's own /cart/shipping_rates.json — the same endpoint
   * the theme's <shipping-calculator> uses — so the figure shown above the
   * subtotal is the one checkout will charge for the cheapest option, not an
   * estimate this file invented.
   */

  var ratesCache = {};

  function isValidZip(zip) {
    return /^\d{5}$/.test(String(zip || '').trim());
  }

  function readStoredZip() {
    try {
      var zip = localStorage.getItem(ZIP_STORAGE_KEY);
      return isValidZip(zip) ? zip.trim() : null;
    } catch (e) {
      // Private browsing, or storage disabled: fall back to asking each time.
      return null;
    }
  }

  function storeZip(zip) {
    try {
      localStorage.setItem(ZIP_STORAGE_KEY, zip);
    } catch (e) {
      /* non-fatal — the rate still resolves for this page view */
    }
  }

  function strings() {
    return config().strings || {};
  }

  function money(amountMajorUnits) {
    var cents = Math.round(parseFloat(amountMajorUnits) * 100);
    if (window.FoxTheme && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
      return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings && FoxTheme.settings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Rates depend on what is in the cart, so the cache is keyed by ZIP and dropped
   * whenever the cart changes. Without that a shopper who adds a heavy item keeps
   * being quoted the lighter cart's price.
   */
  function fetchRates(zip) {
    if (Object.prototype.hasOwnProperty.call(ratesCache, zip)) {
      return Promise.resolve(ratesCache[zip]);
    }

    var cartUrl = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_url) || '/cart';
    var url = (cartUrl + '/shipping_rates.json').replace('//', '/');
    var country = (window.Shopify && window.Shopify.country) || 'US';

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ shipping_address: { zip: zip, country: country } }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (parsed) {
        // A 422 comes back as an errors object rather than shipping_rates.
        var rates = (parsed && parsed.shipping_rates) || [];
        var cheapest = null;
        rates.forEach(function (rate) {
          var price = parseFloat(rate.price);
          if (isNaN(price)) return;
          if (cheapest === null || price < cheapest) cheapest = price;
        });
        ratesCache[zip] = cheapest;
        return cheapest;
      })
      .catch(function () {
        // Network or parse failure: cache nothing, so the next open retries.
        return undefined;
      });
  }

  function setState(widget, state) {
    var prompt = widget.querySelector('[data-delivery-prompt]');
    var result = widget.querySelector('[data-delivery-result]');
    if (prompt) prompt.hidden = state !== 'prompt';
    if (result) result.hidden = state !== 'result';
  }

  function paintAmount(widget, zip, amount) {
    var amountEl = widget.querySelector('[data-delivery-amount]');
    var zipEl = widget.querySelector('[data-delivery-zip]');

    if (zipEl) zipEl.textContent = zip;
    if (!amountEl) return;

    if (amount === null) {
      // The ZIP resolved but nothing ships there.
      amountEl.textContent = strings().none || 'Unavailable';
    } else if (amount === undefined) {
      // Request failed — say so rather than showing a wrong number.
      amountEl.textContent = strings().atCheckout || 'Calculated at checkout';
    } else if (amount === 0) {
      amountEl.textContent = strings().free || 'FREE';
    } else {
      amountEl.textContent = money(amount);
    }
  }

  /**
   * Paints every shipping row in the document. Called on load, after each cart
   * update (the row sits inside the footer, which assets/cart.js replaces
   * wholesale) and whenever a ZIP is submitted.
   */
  function renderEstimates() {
    var widgets = document.querySelectorAll('[data-cart-modern-delivery]');
    if (!widgets.length) return;

    var zip = readStoredZip();

    Array.prototype.forEach.call(widgets, function (widget) {
      var error = widget.querySelector('[data-delivery-error]');
      if (error) error.hidden = true;

      if (!zip) {
        setState(widget, 'prompt');
        return;
      }

      setState(widget, 'result');

      var amountEl = widget.querySelector('[data-delivery-amount]');
      var cached = Object.prototype.hasOwnProperty.call(ratesCache, zip);
      if (!cached && amountEl) {
        amountEl.textContent = strings().calculating || 'Calculating…';
      }

      fetchRates(zip).then(function (amount) {
        // Re-read: the shopper may have cleared or changed the ZIP mid-request.
        if (readStoredZip() !== zip) return;
        paintAmount(widget, zip, amount);
      });
    });
  }

  function submitZip(widget) {
    var input = widget.querySelector('[data-delivery-input]');
    var error = widget.querySelector('[data-delivery-error]');
    if (!input) return;

    var zip = input.value.trim();
    if (!isValidZip(zip)) {
      if (error) {
        error.textContent = error.dataset.message || 'Please enter a valid 5-digit ZIP code.';
        error.hidden = false;
      }
      input.focus();
      return;
    }

    storeZip(zip);
    // Keep the product page's arrival widget in step when both are on screen.
    if (typeof window.calcAndShowDates === 'function') {
      try {
        window.calcAndShowDates(zip);
      } catch (e) {
        /* the product widget is not on this page, or errored on its own */
      }
    }
    renderEstimates();
  }


  /* ------------------------------------------------------- variant picker */

  /*
   * Suggestion rows for multi-variant products open a sheet inside the drawer
   * rather than navigating away to the product page. The picker does NOT add to
   * the cart itself: it writes the chosen variant id into the row's own
   * <form is="product-form"> and submits that, so the add, the drawer re-render
   * and the shipping-insurance re-tier all run through exactly the path a
   * single-variant add already uses. One code path, no duplicated cart logic.
   */

  var productCache = {};
  var activeForm = null;

  function sheet() {
    var host = drawer();
    return host ? host.querySelector('[data-variant-sheet]') : null;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function moneyCents(cents) {
    var amount = Number(cents);
    if (!isFinite(amount)) return '';
    if (window.FoxTheme && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
      try {
        return FoxTheme.Currency.formatMoney(amount, FoxTheme.settings && FoxTheme.settings.moneyFormat);
      } catch (e) {
        /* fall through to the plain format below */
      }
    }
    return '$' + (amount / 100).toFixed(2);
  }

  function t(key, fallback) {
    var s = strings();
    return s && s[key] ? s[key] : fallback;
  }

  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'products/' + encodeURIComponent(handle) + '.js')
      .then(function (res) {
        if (!res.ok) throw new Error('product-fetch-failed');
        return res.json();
      })
      .then(function (data) {
        productCache[handle] = data;
        return data;
      });
  }

  /** Variant whose option values match the current selection exactly, or null. */
  function matchVariant(product, chosen) {
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var hit = true;
      for (var j = 0; j < chosen.length; j++) {
        if (String(v.options[j]) !== String(chosen[j])) {
          hit = false;
          break;
        }
      }
      if (hit) return v;
    }
    return null;
  }

  function readSelection(panel) {
    return [].map.call(panel.querySelectorAll('[data-variant-option]'), function (sel) {
      return sel.value;
    });
  }

  /**
   * Reflects the current selection into the sheet: live price, and whether adding
   * is allowed. On a multi-option product a chosen combination may not resolve to
   * an available variant at all, so the button is disabled and relabelled rather
   * than silently posting a bad id.
   */
  function syncSheet(panel, product) {
    var confirmBtn = panel.querySelector('[data-variant-confirm]');
    var priceEl = panel.querySelector('[data-variant-price]');
    var variant = matchVariant(product, readSelection(panel));

    if (priceEl) {
      var html = '';
      if (variant) {
        html =
          '<span class="cart-drawer--modern__variant-price-now">' + escapeHtml(moneyCents(variant.price)) + '</span>';
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          html +=
            ' <s class="cart-drawer--modern__variant-price-was">' +
            escapeHtml(moneyCents(variant.compare_at_price)) +
            '</s>';
        }
      }
      priceEl.innerHTML = html;
    }

    if (confirmBtn) {
      var ok = !!(variant && variant.available);
      confirmBtn.disabled = !ok;
      confirmBtn.dataset.variantId = variant ? variant.id : '';
      confirmBtn.textContent = ok
        ? t('addToCart', 'Add to cart')
        : variant
        ? t('soldOut', 'Sold out')
        : t('unavailable', 'Unavailable');
    }
  }

  function closeSheet() {
    var el = sheet();
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    var panel = el.querySelector('[data-variant-sheet-panel]');
    if (panel) panel.innerHTML = '';
    activeForm = null;
  }

  /** Writes the chosen variant into the originating row's form and submits it. */
  function submitWithVariant(variantId) {
    if (!activeForm || !variantId) return;
    var form = activeForm;
    var input = form.querySelector('[data-modern-variant-id]');
    if (input) input.value = variantId;
    var submitter = form.querySelector('[data-modern-add]');
    closeSheet();
    // requestSubmit fires a real submit event, which is what ProductForm in
    // assets/theme.js listens for. form.submit() would skip that listener
    // entirely and hard-navigate to /cart.
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  function optionNamesOf(product) {
    // product.options is an array of strings on the .js endpoint in some theme
    // versions and an array of objects in others; normalise to names.
    return (product.options || []).map(function (o) {
      return typeof o === 'string' ? o : o.name;
    });
  }

  function buildFields(product, available) {
    return optionNamesOf(product)
      .map(function (name, index) {
        var seen = {};
        var values = [];
        available.forEach(function (v) {
          var value = v.options[index];
          if (value != null && !seen[value]) {
            seen[value] = true;
            values.push(value);
          }
        });
        var id = 'CartDrawerModern-VariantOption-' + index;
        return (
          '<div class="cart-drawer--modern__variant-field">' +
          '<label class="cart-drawer--modern__variant-label" for="' +
          id +
          '">' +
          escapeHtml(name) +
          '</label>' +
          '<select class="cart-drawer--modern__variant-select" id="' +
          id +
          '" data-variant-option="' +
          index +
          '">' +
          values
            .map(function (v) {
              return '<option value="' + escapeHtml(v) + '">' + escapeHtml(v) + '</option>';
            })
            .join('') +
          '</select>' +
          '</div>'
        );
      })
      .join('');
  }

  function openSheet(button) {
    var el = sheet();
    var panel = el && el.querySelector('[data-variant-sheet-panel]');
    if (!panel) return;

    var handle = button.dataset.productHandle;
    if (!handle) return;

    // Remember which row asked, so Confirm submits that row's own form.
    activeForm = button.closest('form');

    button.classList.add('btn--loading');
    fetchProduct(handle)
      .then(function (product) {
        var available = (product.variants || []).filter(function (v) {
          return v.available;
        });

        // Nothing left to choose (stock moved since the drawer rendered): add the
        // one available variant directly rather than showing a one-option sheet.
        if (available.length <= 1) {
          if (available.length === 1) submitWithVariant(available[0].id);
          return;
        }

        var image = product.featured_image || (product.images && product.images[0]) || '';
        var imageHtml = image
          ? '<span class="cart-drawer--modern__variant-img" style="background-image: url(' +
            escapeHtml(JSON.stringify(String(image))) +
            ');"></span>'
          : '';

        panel.innerHTML =
          '<button type="button" class="cart-drawer--modern__variant-close" data-variant-close aria-label="' +
          escapeHtml(t('close', 'Close')) +
          '">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
          '</button>' +
          '<div class="cart-drawer--modern__variant-head">' +
          imageHtml +
          '<span class="cart-drawer--modern__variant-head-text">' +
          '<span class="cart-drawer--modern__variant-title" id="CartDrawerModern-VariantSheet-Title">' +
          escapeHtml(product.title) +
          '</span>' +
          '<span class="cart-drawer--modern__variant-price" data-variant-price></span>' +
          '</span>' +
          '</div>' +
          buildFields(product, available) +
          '<button type="button" class="cart-drawer--modern__variant-confirm btn btn--primary" data-variant-confirm></button>';

        // Open on the first available variant so the sheet never starts on a
        // sold-out combination.
        var first = available[0];
        [].forEach.call(panel.querySelectorAll('[data-variant-option]'), function (sel, index) {
          var want = first.options[index];
          if (want != null) sel.value = want;
        });

        panel.dataset.handle = handle;
        syncSheet(panel, product);

        el.classList.add('is-open');
        el.setAttribute('aria-hidden', 'false');
        var firstSelect = panel.querySelector('.cart-drawer--modern__variant-select');
        if (firstSelect) firstSelect.focus();
      })
      .catch(function (err) {
        console.error('Cart drawer variant picker:', err);
        // Never dead-end: fall back to the form's default (first available) variant.
        var form = activeForm;
        if (form && typeof form.requestSubmit === 'function') {
          form.requestSubmit(form.querySelector('[data-modern-add]'));
        }
      })
      .then(function () {
        button.classList.remove('btn--loading');
      });
  }

  /* ------------------------------------------------------------- wiring up */

  // Delegated on the document: every cart mutation replaces the drawer's
  // contents, so listeners bound to rows or to the sheet would be orphaned.
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var addBtn = target.closest('[data-modern-add]');
    if (addBtn) {
      var count = parseInt(addBtn.dataset.variantCount, 10) || 1;
      // Single variant: leave the normal form submit completely alone.
      if (count <= 1) return;
      event.preventDefault();
      openSheet(addBtn);
      return;
    }

    if (target.closest('[data-variant-close]')) {
      event.preventDefault();
      closeSheet();
      return;
    }

    var confirmBtn = target.closest('[data-variant-confirm]');
    if (confirmBtn) {
      event.preventDefault();
      if (!confirmBtn.disabled) submitWithVariant(confirmBtn.dataset.variantId);
      return;
    }

    // Scrim click closes; a click inside the panel must not.
    var sheetEl = target.closest('[data-variant-sheet]');
    if (sheetEl && !target.closest('[data-variant-sheet-panel]')) {
      closeSheet();
      return;
    }

    var submit = event.target.closest('[data-delivery-submit]');
    if (submit) {
      event.preventDefault();
      var widget = submit.closest('[data-cart-modern-delivery]');
      if (widget) submitZip(widget);
      return;
    }

    var change = event.target.closest('[data-delivery-change]');
    if (change) {
      event.preventDefault();
      var changeWidget = change.closest('[data-cart-modern-delivery]');
      if (!changeWidget) return;
      var prompt = changeWidget.querySelector('[data-delivery-prompt]');
      var result = changeWidget.querySelector('[data-delivery-result]');
      if (prompt) prompt.hidden = false;
      if (result) result.hidden = true;
      var input = changeWidget.querySelector('[data-delivery-input]');
      if (input) {
        input.value = readStoredZip() || '';
        input.focus();
      }
    }
  });

  // Re-price the sheet whenever a variant option changes.
  document.addEventListener('change', function (event) {
    var sel = event.target;
    if (!sel || !sel.matches || !sel.matches('[data-variant-option]')) return;
    var panel = sel.closest('[data-variant-sheet-panel]');
    if (!panel) return;
    var product = productCache[panel.dataset.handle];
    if (product) syncSheet(panel, product);
  });

  /*
   * Escape closes the sheet only, leaving the drawer open behind it.
   *
   * ModalComponent (assets/theme.js) closes the drawer from a `keyup` listener
   * bound to document during connectedCallback — which runs before this file, so
   * it is ahead of us in the bubble-phase list. Handling Escape on `keydown`
   * would not help either: the drawer's `keyup` still arrives afterwards and one
   * press would shut both. Capture phase puts this ahead of every document
   * listener regardless of registration order, and stopImmediatePropagation
   * keeps the drawer's handler from ever seeing the key.
   */
  document.addEventListener(
    'keyup',
    function (event) {
      if (event.code !== 'Escape') return;
      var el = sheet();
      if (!el || !el.classList.contains('is-open')) return;
      event.stopImmediatePropagation();
      closeSheet();
    },
    true
  );

  // Enter in the ZIP field submits it rather than the cart form it sits inside.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    var input = event.target.closest('[data-delivery-input]');
    if (!input) return;
    event.preventDefault();
    var widget = input.closest('[data-cart-modern-delivery]');
    if (widget) submitZip(widget);
  });

  document.addEventListener('cart:updated', function () {
    // Rates are quoted against the server-side cart, so a changed cart
    // invalidates every cached figure — otherwise a shopper who adds a heavy
    // item keeps being shown the lighter cart's price.
    ratesCache = {};
    // The footer — and with it the shipping row — has just been replaced.
    renderEstimates();
    scheduleTransitionCheck();
  });

  document.addEventListener('cart:refresh', function () {
    ratesCache = {};
    renderEstimates();
  });

  function init() {
    renderEstimates();
    // Warmed up front so the first unlock draws bees rather than dropping them
    // while the sprite decodes.
    loadBeeImage();

    var host = drawer();
    if (!host) return;

    // Baseline reading, so the first cart:updated can detect a real transition.
    wasUnlocked = readUnlockedState();

    // ModalComponent (assets/theme.js) sets `active` on the host once the panel
    // has finished sliding in — the right moment for an already-unlocked cart.
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.attributeName !== 'active') return;
        if (host.hasAttribute('active')) {
          renderEstimates();
          checkOpenState();
        }
      });
    }).observe(host, { attributes: true, attributeFilter: ['active'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
